// Règles de progression : composition de chaque niveau et verdict de fin.
import {
  BASE_WORLD_SIZE, BASE_MIN_SPACING, SPACING_GROWTH, GHOST_SPEED_GROWTH, MAX_LEVEL, LEVEL_FAMILY_SIZES,
} from '../config/constants.js';
import { FAMILY_MEMBERS } from '../data/family.js';

/** Kamis dans la famille à ce niveau (LEVEL_FAMILY_SIZES) : 5 au niveau 1, les 15 au dernier. */
export function familySizeForLevel(level) {
  const index = Math.max(1, Math.min(LEVEL_FAMILY_SIZES.length, Math.floor(level))) - 1;
  return LEVEL_FAMILY_SIZES[index];
}

/**
 * Nombre de kamis dans la famille : celui du niveau, ou le réglage « taille de la famille » s'il en impose un (> 0), borné à la famille.
 * @param {{ level: number, setting: number }} options
 */
export function effectiveFamilySize({ level, setting }) {
  const forced = Math.floor(setting) > 0 ? Math.min(FAMILY_MEMBERS.length, Math.floor(setting)) : 0;
  return forced || familySizeForLevel(level);
}

/**
 * Paramètres d'un niveau : la famille compte familySizeForLevel(N) kamis (5 au niveau 1 … 15 au dernier, Ujin seulement
 * au dernier) et le niveau N en met N exemplaires de chaque sur la carte — au niveau 1 un seul de chaque, au niveau 2 deux
 * identiques de chaque, etc. La cible à photographier reste les types de la famille (voir classifyResult) : les doublons
 * ne servent qu'à les repérer parmi plus de monde, dans un monde plus grand et plus espacé.
 * @param {number} level  1..MAX_LEVEL
 * @param {{ worldGrowth:number, photoCostPercent:number, ghostSpeed:number }} settings
 */
export function getLevelParams(level, { worldGrowth, photoCostPercent, ghostSpeed, familySize = 0 }) {
  const clampedLevel = Math.max(1, Math.min(MAX_LEVEL, Math.floor(level)));
  const n = clampedLevel - 1;
  // familySize : imposé (1..15) ou 0 / absent = celui du niveau
  const size = Math.floor(familySize) > 0 ? Math.min(FAMILY_MEMBERS.length, Math.floor(familySize)) : familySizeForLevel(clampedLevel);
  const ghostCount = size * clampedLevel;
  return {
    familySize: size,
    ghostCount,
    worldSize: BASE_WORLD_SIZE + n * worldGrowth,
    minSpacing: BASE_MIN_SPACING + n * SPACING_GROWTH,
    filmShots: getFilmShots(photoCostPercent),
    // Fantômes immobiles (réglage dev à 0) : ils le restent à tous les niveaux
    ghostSpeed: ghostSpeed > 0 ? ghostSpeed + n * GHOST_SPEED_GROWTH : 0,
  };
}

/**
 * Photos que contient une batterie pleine : une photo coûte `photoCostPercent` % de la batterie (5 % = 20 photos), à tous
 * les niveaux. Le temps de jeu vide aussi la batterie, indépendamment (voir core/battery.js) : plus on prend de photos,
 * plus elle baisse vite.
 */
export function getFilmShots(photoCostPercent) {
  return Math.max(1, Math.round(100 / Math.max(1, photoCostPercent)));
}

/**
 * Verdict à la fin du niveau.
 * - 'lose'    : pellicule vide alors qu'il reste des types de kami à trouver
 * - 'win'     : les 15 types trouvés en gâchant au plus la moitié des vues de réserve
 * - 'survive' : les 15 types trouvés, mais de justesse
 * La cible est toujours `familyCount` (15, indépendant du niveau) : un doublon photographié par
 * erreur compte comme une vue gâchée dans le bilan, comme une photo ratée — jamais comme du travail
 * en plus à faire, ni comme une capture qui n'aurait servi à rien.
 * @param {number} remainingTypes  types de kami pas encore photographiés (0..familyCount)
 * @param {number} [shotsLeft]  vues restantes si seules les photos vidaient la pellicule : le temps qui passe vide
 *   aussi la batterie (voir core/battery.js) mais ne compte pas comme une vue gâchée dans le bilan win/survive
 * @returns {'lose'|'win'|'survive'|null} null si le niveau n'est pas terminé
 */
export function classifyResult({ remainingTypes, filmLeft, filmShots, familyCount, shotsLeft = filmLeft }) {
  if (remainingTypes > 0) return filmLeft <= 0 ? 'lose' : null;
  const spare = filmShots - familyCount;             // vues qu'on pouvait rater
  const missed = filmShots - shotsLeft - familyCount; // vues effectivement ratées (ratés + doublons)
  return missed <= Math.floor(spare / 2) ? 'win' : 'survive';
}
